import { isPromiseLike, timestampMs } from 'utils';
import { errorMessageFactory } from 'factories/api';
import uuidv4 from 'utils/uuid';
import type { AnyAction, Reducer } from 'redux';
import { actionCreatorFactory } from 'typescript-fsa';
import type { AsyncActionCreators } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { Thunk } from 'types/redux';
import { max } from 'lodash-es';
import type { DeepReadonly } from 'types/utility';

/**
 * xhrStatus is a collection of re-usable reducer and utility functions that
 * makes it simpler to implement fetching status in redux modules, selectors, etc.
 */

export interface Signature {
  id: string;
  key: string;
  timestamp: number;
}
export interface XhrErrorSignature extends Signature {
  error: string;
}

export interface XhrStatus {
  fetching: Array<Signature>;
  fetched: Array<Signature>;
  errors: Array<XhrErrorSignature>;
  saving: Array<Signature>;
  saved: Array<Signature>;
}

/**
 * Use this in selectors when you want to know the UI states to respond to.
 */
export interface XhrStatusUIState {
  isSaving: boolean;
  isLoading: boolean;
  hasInitialized: boolean;
  hasErrors: boolean;
  errors: Array<XhrErrorSignature>;
  lastFetch: Date | undefined;
}

export function xhrStatusUIState(xhrStatus: XhrStatus, key?: string): XhrStatusUIState {
  if (key !== undefined) {
    return {
      isSaving: xhrStatus.saving.some(c => c.key === key),
      isLoading: xhrStatus.fetching.some(c => c.key === key),
      hasInitialized: xhrStatus.fetched.some(c => c.key === key),
      hasErrors: xhrStatus.errors.some(c => c.key === key),
      errors: xhrStatus.errors.filter(c => c.key === key),
      lastFetch: max(
        xhrStatus.fetched.filter(c => c.key === key).map(sig => new Date(sig.timestamp))
      ),
    };
  }

  return {
    isSaving: xhrStatus.saving.length > 0,
    isLoading: xhrStatus.fetching.length > 0,
    hasInitialized: xhrStatus.fetched.length > 0,
    hasErrors: xhrStatus.errors.length > 0,
    errors: xhrStatus.errors,
    lastFetch: max(xhrStatus.fetched.map(sig => new Date(sig.timestamp))),
  };
}

/**
 * Use this in selectors to determine if we should be (re)fetching XHR endpoints
 */
interface XhrStatusQueryInfoOptions {
  /** If fetched.timestamp is more than maxMinuteAge, it can/should be refetched */
  maxMinuteAge?: number;
  key?: string;
}

interface XhrStatusQueryInfo {
  shouldFetch: boolean;
  isInFlight: boolean;
  keysToFetch: Array<string>;
}

export function xhrStatusQueryInfo(
  xhrStatus: XhrStatus,
  { maxMinuteAge = 5, key = 'default' }: XhrStatusQueryInfoOptions = {}
): XhrStatusQueryInfo {
  const outdatedFetches = xhrStatus.fetched.filter(
    s => s.key === key && s.timestamp < timestampMs() - maxMinuteAge * 60 * 1000
  );
  // If this key outdated, as determined by it's max age?
  const isOutdated = outdatedFetches.length > 0;
  // Are we already fetching this?
  const isInFlight = xhrStatus.fetching.some(s => s.key === key);
  // Did we already fetch this?
  const isFetched = xhrStatus.fetched.some(s => s.key === key);

  return {
    isInFlight,
    shouldFetch: !isInFlight && (isOutdated || !isFetched),
    // Not sure if this makes sense anymore...
    // See projects.actions as the non-basic implementation
    keysToFetch: outdatedFetches.map(s => s.key),
  };
}

/** Use this in redux modules to generate the initial state */
export function xhrStatusFactory(overwrites: Partial<XhrStatus> = {}): XhrStatus {
  return {
    fetching: overwrites.fetching || [],
    fetched: overwrites.fetched || [],
    errors: overwrites.errors || [],
    saving: overwrites.saving || [],
    saved: overwrites.saved || [],
  };
}

interface XhrStatusActionParams {
  id: string;
  key: string;
  startedTimestamp: number;
}

interface XhrStatusActionResult {
  doneTimestamp: number;
}

interface XhrStatusActionError {
  errorMessage: string;
  failedTimestamp: number;
}

type ActionType = AsyncActionCreators<
  XhrStatusActionParams,
  XhrStatusActionResult,
  XhrStatusActionError
>;

interface AbortablePromiseFactory<T> {
  (abortSignal: AbortSignal): Promise<T>;
}

type XhrWrapper = <T>(
  p: Promise<T> | AbortablePromiseFactory<T>,
  key?: string
) => Thunk<Promise<T>>;

export interface XhrStatusModule {
  withXhrStatusReducer: <S extends { xhrStatus: DeepReadonly<XhrStatus> }>(
    reducer: Reducer<S, AnyAction>
  ) => Reducer<S, AnyAction>;
  reducer: (state: DeepReadonly<XhrStatus>, action: AnyAction) => DeepReadonly<XhrStatus>;
  wrapFetching: XhrWrapper;
  wrapSaving: XhrWrapper;

  /**
   * Aborts one or more fetch requests.
   * @param keyOrPattern Key or pattern of the key.
   * - If a string, it will abort a single request matching the exact key.
   * - If a regex, it will abort all requests matching the pattern.
   * - If undefined, it will abort a single request matching the default key.
   * @returns `true` if a request was found and aborted.
   */
  abortFetching: (keyOrPattern?: string | RegExp) => boolean;

  xhrStatusActions: {
    fetchAction: ActionType;
    saveAction: ActionType;
  };
}

export function createXhrStatusModule(prefix: string): XhrStatusModule {
  const actionCreator = actionCreatorFactory(`timely/${prefix}`);

  const fetchAction = actionCreator.async<
    XhrStatusActionParams,
    XhrStatusActionResult,
    XhrStatusActionError
  >('xhrStatus/FETCH');
  const saveAction = actionCreator.async<
    XhrStatusActionParams,
    XhrStatusActionResult,
    XhrStatusActionError
  >('xhrStatus/SAVE');

  const withXhrStatusReducer = <S extends { xhrStatus: DeepReadonly<XhrStatus> }>(
    parentReducer: Reducer<S, AnyAction>
  ): Reducer<S, AnyAction> => {
    const initialState = parentReducer(undefined, { type: '__xhr_initial_state' });

    const mapXhrStatus =
      <T>(mapper: (subState: DeepReadonly<XhrStatus>, action: T) => Partial<XhrStatus>) =>
      (state: S, action: T): S => {
        return {
          ...state,
          xhrStatus: {
            ...state.xhrStatus,
            ...mapper(state.xhrStatus, action),
          },
        };
      };

    return reducerWithInitialState(initialState)
      .caseWithAction(
        fetchAction.started,
        mapXhrStatus((state, action) => {
          const { id, key, startedTimestamp } = action.payload;
          const fetchingExceptCurrent = state.fetching.filter(signature => signature.id !== id);
          const errorsExceptKey = state.errors.filter(signature => signature.key !== key);
          return {
            errors: errorsExceptKey,
            fetching: [...fetchingExceptCurrent, { id, key, timestamp: startedTimestamp }],
          };
        })
      )
      .caseWithAction(
        saveAction.started,
        mapXhrStatus((state, action) => {
          const { id, key, startedTimestamp } = action.payload;
          const savingExceptCurrent = state.saving.filter(signature => signature.id !== id);
          const errorsExceptKey = state.errors.filter(signature => signature.key !== key);
          return {
            errors: errorsExceptKey,
            saving: [...savingExceptCurrent, { id, key, timestamp: startedTimestamp }],
          };
        })
      )
      .caseWithAction(
        fetchAction.done,
        mapXhrStatus((state, action) => {
          const { id, key } = action.payload.params;
          const errorsExceptKey = state.errors.filter(signature => signature.key !== key);
          const fetchedExceptKey = state.fetched.filter(signature => signature.key !== key);
          const fetchingExceptCurrent = state.fetching.filter(signature => signature.id !== id);

          return {
            errors: errorsExceptKey,
            fetching: fetchingExceptCurrent,
            fetched: [
              ...fetchedExceptKey,
              { id, key, timestamp: action.payload.result.doneTimestamp },
            ],
          };
        })
      )
      .caseWithAction(
        saveAction.done,
        mapXhrStatus((state, action) => {
          const { id, key } = action.payload.params;
          const errorsExceptKey = state.errors.filter(signature => signature.key !== key);
          const savedExceptKey = state.saving.filter(signature => signature.key !== key);
          const savingExceptCurrent = state.saving.filter(signature => signature.id !== id);

          return {
            errors: errorsExceptKey,
            saving: savingExceptCurrent,
            saved: [...savedExceptKey, { id, key, timestamp: action.payload.result.doneTimestamp }],
          };
        })
      )
      .casesWithAction(
        [fetchAction.failed, saveAction.failed],
        mapXhrStatus((state, action) => {
          const { params, error } = action.payload;
          const { id, key } = params;
          const fetchingExceptCurrent = state.fetching.filter(signature => signature.id !== id);
          const savingExceptCurrent = state.saving.filter(signature => signature.id !== id);
          const errorsExceptKey = state.errors.filter(signature => signature.key !== key);
          return {
            fetching: fetchingExceptCurrent,
            errors: [
              ...errorsExceptKey,
              { id, key, error: error.errorMessage, timestamp: error.failedTimestamp },
            ],
            saving: savingExceptCurrent,
          };
        })
      )
      .default(parentReducer);
  };

  const singletonReducer = withXhrStatusReducer(s => s as any);
  const reducer = (state: DeepReadonly<XhrStatus>, action: AnyAction): DeepReadonly<XhrStatus> =>
    singletonReducer({ xhrStatus: state }, action).xhrStatus;

  const wrapperFactory = (
    asyncAction: AsyncActionCreators<
      XhrStatusActionParams,
      XhrStatusActionResult,
      XhrStatusActionError
    >
  ): [XhrWrapper, Record<string, AbortController>] => {
    const abortControllers: Record<string, AbortController> = {};
    return [
      function wrapped<T>(
        promiseOrFactory: Promise<T> | AbortablePromiseFactory<T>,
        key: string = 'default'
      ): Thunk<Promise<T>> {
        return dispatch => {
          let abortController: AbortController | undefined;
          const promise = isPromiseLike<T>(promiseOrFactory)
            ? promiseOrFactory
            : (() => {
                abortController = new AbortController();
                return promiseOrFactory(abortController.signal);
              })();

          if (abortController) {
            abortControllers[key]?.abort();
            abortControllers[key] = abortController;
          }

          const id = uuidv4();
          const params = { id, key, startedTimestamp: timestampMs() };
          dispatch(asyncAction.started(params));
          return promise.then(
            (...args) => {
              setTimeout(() => {
                // Let's allow handlers to process before dispatching FETCHED
                dispatch(asyncAction.done({ params, result: { doneTimestamp: timestampMs() } }));
              });
              if (abortControllers[key] === abortController) {
                delete abortControllers[key];
              }
              return Promise.resolve(...args);
            },
            e => {
              dispatch(
                asyncAction.failed({
                  params,
                  error: { errorMessage: errorMessageFactory(e), failedTimestamp: timestampMs() },
                })
              );
              if (abortControllers[key] === abortController) {
                delete abortControllers[key];
              }
              return Promise.reject(e);
            }
          );
        };
      },
      abortControllers,
    ];
  };
  const [wrapFetching, fetchingAbortControllers] = wrapperFactory(fetchAction);
  const [wrapSaving] = wrapperFactory(saveAction);

  const abortFetching = (keyOrPattern: string | RegExp = 'default') => {
    if (keyOrPattern instanceof RegExp) {
      let didAbort = false;
      Object.keys(fetchingAbortControllers).forEach(key => {
        if (!keyOrPattern.test(key)) return;

        const abortController = fetchingAbortControllers[key];
        if (!abortController) return;

        abortController.abort();
        didAbort = true;
      });
      return didAbort;
    } else {
      const abortController = fetchingAbortControllers[keyOrPattern];
      if (!abortController) return false;

      abortController.abort();
      return true;
    }
  };

  return {
    withXhrStatusReducer,
    reducer,
    wrapFetching,
    wrapSaving,
    abortFetching,
    xhrStatusActions: {
      fetchAction,
      saveAction,
    },
  };
}

/**
 * Determines if an error was caused by an aborted request.
 */
export const isAbortError = (error: Error): error is DOMException => {
  return error instanceof DOMException && error.code === error.ABORT_ERR;
};
